import { toDisplayText } from './model';

const VALID_QUALITIES = ['good', 'partial', 'adjusted'];
const DEGRADED_QUALITIES = ['partial', 'adjusted'];
const QUALITY_PRIORITY = { good: 0, partial: 1, adjusted: 2, missing: 3, invalid: 4 };

const incomingNodes = (graph, node, port) => (graph?.edges || [])
  .filter(edge => edge.target_node_key === node.node_key && (!port || edge.target_port === port))
  .sort((left, right) => Number(left.input_order || 0) - Number(right.input_order || 0))
  .map(edge => (graph?.nodes || []).find(candidate => candidate.node_key === edge.source_node_key))
  .filter(Boolean);

const mergeQuality = qualities => [...qualities]
  .filter(Boolean)
  .sort((left, right) => (QUALITY_PRIORITY[right] ?? 0) - (QUALITY_PRIORITY[left] ?? 0))[0] || 'good';

const unsafe = reason => ({ shape: 'series', additive: false, reason });

export const analyzeNodeSemantics = (graph, node, visited = new Set()) => {
  if (!node) return unsafe('No se pudo identificar el origen del resultado.');
  if (visited.has(node.node_key)) return unsafe('El modelo contiene una dependencia circular.');
  const nextVisited = new Set(visited).add(node.node_key);
  const config = node.config_json || {};

  if (node.type === 'constant.number' || node.type === 'variable.manual') {
    return { shape: 'scalar', additive: false };
  }
  if (node.type === 'source.basic_parameter' || node.type === 'source.legacy_calculated_parameter') {
    const additive = config.aggregation === 'delta' || config.aggregation === 'sum';
    return additive
      ? { shape: 'series', additive: true }
      : unsafe('Este resultado usa mediciones que no representan valores acumulables en el período.');
  }

  if (node.type === 'output.number' || node.type === 'math.percentage') {
    const input = incomingNodes(graph, node)[0];
    return analyzeNodeSemantics(graph, input, nextVisited);
  }

  if (node.type === 'math.add' || node.type === 'math.subtract') {
    const inputs = incomingNodes(graph, node).map(input => analyzeNodeSemantics(graph, input, nextVisited));
    if (inputs.length && inputs.every(input => input.shape === 'scalar')) return { shape: 'scalar', additive: false };
    if (inputs.length && inputs.every(input => input.shape === 'series' && input.additive)) return { shape: 'series', additive: true };
    return unsafe('La suma o resta combina valores cuya agregación para todo el período no es segura.');
  }

  if (node.type === 'math.multiply' || node.type === 'math.divide') {
    const inputs = incomingNodes(graph, node).map(input => analyzeNodeSemantics(graph, input, nextVisited));
    if (inputs.length === 2 && inputs.every(input => input.shape === 'scalar')) return { shape: 'scalar', additive: false };
    const series = inputs.filter(input => input.shape === 'series');
    const scalars = inputs.filter(input => input.shape === 'scalar');
    const seriesIsNumerator = node.type !== 'math.divide' || inputs[0]?.shape === 'series';
    if (series.length === 1 && scalars.length === 1 && series[0].additive && seriesIsNumerator) {
      return { shape: 'series', additive: true };
    }
    return unsafe('La multiplicación o división entre series no puede consolidarse de forma segura.');
  }

  return unsafe('El modelo contiene una operación sin semántica de período reconocida.');
};

export const outputsFromGraph = graph => (graph?.nodes || [])
  .filter(node => node.type === 'output.number')
  .map(node => {
    const lineage = analyzeNodeSemantics(graph, node);
    const explicitReducer = node.config_json?.period_reducer || null;
    const periodReducer = explicitReducer || (lineage.additive ? 'sum' : null);
    return {
      key: node.config_json?.key,
      name: toDisplayText(node.name, toDisplayText(node.config_json?.key, 'Resultado')),
      unit: node.config_json?.unit || '1',
      semantics: {
        ...lineage,
        periodReducer,
        explicitReducer: Boolean(explicitReducer),
        reducible: lineage.shape === 'scalar' || Boolean(periodReducer),
        reason: periodReducer ? null : lineage.reason,
      },
    };
  })
  .filter(output => output.key);

export const manualVariablesFromGraph = graph => (graph?.nodes || [])
  .filter(node => node.type === 'variable.manual')
  .map(node => ({
    key: node.config_json?.key,
    name: toDisplayText(node.name, toDisplayText(node.config_json?.key, 'Variable manual')),
    unit: node.config_json?.unit || '1',
    required: !['zero', 'null'].includes(node.config_json?.missing_policy),
  }))
  .filter(variable => variable.key);

export const consolidateExecutionOutput = (output, semantics) => {
  if (!output) return { valid: false, quality: 'missing', message: 'La ejecución no devolvió el resultado seleccionado.' };
  if (output.shape === 'scalar') {
    const quality = output.quality || (output.value == null ? 'missing' : 'good');
    if (!VALID_QUALITIES.includes(quality) || output.value == null || !Number.isFinite(Number(output.value))) {
      return { valid: false, quality, message: 'El resultado no contiene un valor válido para comparar.' };
    }
    return { valid: true, value: Number(output.value), unit: output.unit || '1', quality, degraded: DEGRADED_QUALITIES.includes(quality) };
  }
  if (output.shape !== 'series') return { valid: false, quality: 'invalid', message: 'El formato del resultado no es compatible con esta comparación.' };
  const reducer = semantics?.periodReducer || (semantics?.additive ? 'sum' : null);
  if (!reducer) return { valid: false, quality: 'invalid', unsupported: true, message: semantics?.reason || 'Esta serie no puede consolidarse de forma segura.' };
  const points = Array.isArray(output.points) ? output.points : [];
  if (!points.length) return { valid: false, quality: 'missing', message: 'No se encontraron mediciones para el período seleccionado.' };
  const normalized = points.map((point, index) => ({
    ...point,
    index,
    numericValue: Number(point.value),
    normalizedQuality: point.quality || (point.value == null ? 'missing' : 'good'),
  }));
  const incompletePeriod = normalized.some(point => (
    !VALID_QUALITIES.includes(point.normalizedQuality)
    || point.value == null
    || point.value === ''
    || !Number.isFinite(point.numericValue)
  ));
  if (incompletePeriod) {
    return {
      valid: false,
      quality: mergeQuality(normalized.map(point => (
        ['missing', 'invalid'].includes(point.normalizedQuality)
          ? point.normalizedQuality
          : point.value == null || point.value === '' || !Number.isFinite(point.numericValue)
            ? 'invalid'
            : point.normalizedQuality
      ))),
      message: 'No se pudo obtener un resultado completo para este período porque faltan mediciones válidas.',
    };
  }
  const validPoints = normalized;
  const quality = mergeQuality(validPoints.map(point => point.normalizedQuality));
  let value;
  if (reducer === 'sum') value = validPoints.reduce((total, point) => total + point.numericValue, 0);
  if (reducer === 'max') value = Math.max(...validPoints.map(point => point.numericValue));
  if (reducer === 'min') value = Math.min(...validPoints.map(point => point.numericValue));
  if (reducer === 'last') {
    const ordered = [...validPoints].sort((left, right) => {
      const byTimestamp = String(left.timestamp || '').localeCompare(String(right.timestamp || ''));
      return byTimestamp || left.index - right.index;
    });
    const last = ordered[ordered.length - 1];
    value = last.numericValue;
  }
  if (reducer === 'avg') {
    if (validPoints.length === 1) value = validPoints[0].numericValue;
    else {
      const weighted = validPoints.every(point => Number.isFinite(Number(point.sample_count)) && Number(point.sample_count) > 0);
      if (!weighted) {
        return {
          valid: false, quality: 'invalid', unsupported: true,
          message: 'El promedio del período no puede calcularse con precisión porque los buckets no informan cuántas muestras contienen.',
        };
      }
      const totalSamples = validPoints.reduce((total, point) => total + Number(point.sample_count), 0);
      value = validPoints.reduce((total, point) => total + point.numericValue * Number(point.sample_count), 0) / totalSamples;
    }
  }
  if (!Number.isFinite(value)) return { valid: false, quality: 'invalid', message: 'El resultado consolidado no es numéricamente válido.' };
  return {
    valid: true,
    value,
    unit: output.unit || '1',
    quality,
    degraded: DEGRADED_QUALITIES.includes(quality),
  };
};

const parseDateOnly = value => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateOnly = date => date.toISOString().slice(0, 10);

export const addCalendarDays = (value, days) => {
  const date = parseDateOnly(value);
  if (!date) return '';
  date.setUTCDate(date.getUTCDate() + days);
  return formatDateOnly(date);
};

export const inclusiveDayCount = (start, end) => {
  const from = parseDateOnly(start);
  const to = parseDateOnly(end);
  if (!from || !to || from > to) return 0;
  return Math.round((to - from) / 86400000) + 1;
};

export const suggestedComparisonPeriods = project => {
  const start = parseDateOnly(String(project?.start_date || '').slice(0, 10));
  if (!start) return { referenceStart: '', referenceEnd: '', evaluationStart: '', evaluationEnd: '' };
  const evaluationStart = formatDateOnly(start);
  const configuredEnd = parseDateOnly(String(project?.actual_end_date || project?.planned_end_date || '').slice(0, 10));
  const nextMonth = new Date(start);
  nextMonth.setUTCMonth(nextMonth.getUTCMonth() + 1);
  const evaluationEnd = configuredEnd && configuredEnd >= start
    ? formatDateOnly(configuredEnd)
    : formatDateOnly(new Date(nextMonth.getTime() - 86400000));
  const days = inclusiveDayCount(evaluationStart, evaluationEnd);
  const referenceEnd = addCalendarDays(evaluationStart, -1);
  return {
    referenceStart: addCalendarDays(referenceEnd, -(days - 1)),
    referenceEnd,
    evaluationStart,
    evaluationEnd,
  };
};

export const executionPeriod = (start, inclusiveEnd) => ({
  startDate: `${start}T00:00:00-03:00`,
  endDate: `${addCalendarDays(inclusiveEnd, 1)}T00:00:00-03:00`,
});

export const calculateVariation = (reference, evaluation) => {
  const absolute = Number(evaluation) - Number(reference);
  return { absolute, percentage: Number(reference) === 0 ? null : absolute / Number(reference) * 100 };
};

export const deduplicateExecutionWarnings = warnings => {
  const seen = new Set();
  return (Array.isArray(warnings) ? warnings : []).filter(warning => {
    const key = [warning?.period || '', warning?.code || '', warning?.message || ''].join('|');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const economicImpactPayload = ({ evaluationStart, evaluationEnd, energyVariation, energyUnit }) => ({
  period: { start: evaluationStart, end: evaluationEnd },
  energy_variation: { value: energyVariation, unit: energyUnit },
});
